// pms-adapters / supabase / folioMoney — factures, avoirs and payments on a
// PMS folio (P3.1 InvoicingService, P3.2 PaymentService), for the desk of an
// établissement the PMS holds (PMS-OWNED-BOOKINGS-BRIEF OB6).
//
// Every write is a desk wrapper the database gates itself (`pms_issue_invoice`,
// `pms_issue_credit_note`, `pms_receive_payment`, `pms_reverse_payment`,
// `pms_refund_payment`, `pms_transfer_payment`, `pms_forfeit_payment`,
// `pms_checkout_folio`), each creating a NEW chained record or movement. The
// `gate()` here is the L2 courtesy (ADR-0013 §4), not the boundary. Reads go
// through the tables' RLS (pms.invoice.read / pms.payment.read).
//
// Until the établissement is live, every document and payment lands on its
// école caisse (`is_training`), outside every total.

import { CAPABILITIES, assertCapability, type ActorSource, type Capability } from '@lemontaigu/platform';
import {
  err,
  ok,
  type Cents,
  type CheckoutPayment,
  type Err,
  type FiscalDocument,
  type FiscalDocumentId,
  type FiscalLineId,
  type FolioId,
  type FolioPosition,
  type InvoicingService,
  type IsoDate,
  type Payment,
  type PaymentId,
  type PaymentService,
  type Result,
  type TerminalTransactionId,
} from '@lemontaigu/pms-core';
import { fromList, fromResult, type SupabaseClient } from './support.js';

function gate(actor: ActorSource, cap: Capability): Err | null {
  const check = assertCapability(actor.current(), cap);
  return check.ok ? null : err('forbidden', check.message);
}

interface DocumentRow {
  id: number;
  caisse_id: number;
  kind: FiscalDocument['kind'];
  is_training: boolean;
  series: string;
  number: number;
  sequence_number: number;
  issued_at: string;
  folio_id: number | null;
  credits_document_id: number | null;
  total_ttc_cents: number;
  // Stored snake_case with numeric strings ('24183'); Number() reads either.
  vat_breakdown: { rate: string | null; ttc_cents?: number | string; ht_cents?: number | string;
                   vat_cents?: number | string }[] | null;
  chain_hash: string;
  pms_fiscal_line?: LineRow[];
}

interface LineRow {
  id: number;
  line_no: number;
  kind: 'room' | 'product' | 'tax';
  label_fr: string;
  quantity: number;
  unit_cents: number;
  amount_cents: number;
  tva_rate: string | number | null;
  service_start: string;
  service_end: string;
  credits_line_id: number | null;
}

const DOCUMENT_SELECT =
  'id, caisse_id, kind, is_training, series, number, sequence_number, issued_at, folio_id, credits_document_id, ' +
  'total_ttc_cents, vat_breakdown, chain_hash, pms_fiscal_line(id, line_no, kind, label_fr, quantity, unit_cents, ' +
  'amount_cents, tva_rate, service_start, service_end, credits_line_id)';

const rate = (r: string | number | null): string | null => (r === null ? null : Number(r).toFixed(2));

export function rowToDocument(r: DocumentRow): FiscalDocument {
  return {
    id: r.id,
    caisseId: r.caisse_id,
    kind: r.kind,
    isTraining: r.is_training,
    series: r.series,
    number: Number(r.number),
    sequenceNumber: Number(r.sequence_number),
    issuedAt: r.issued_at,
    folioId: r.folio_id,
    creditsDocumentId: r.credits_document_id,
    totalTtcCents: Number(r.total_ttc_cents),
    vatBreakdown: (r.vat_breakdown ?? []).map((v) => ({
      rate: v.rate === null || v.rate === undefined ? null : rate(v.rate),
      ttcCents: Number(v.ttc_cents ?? 0),
      htCents: Number(v.ht_cents ?? 0),
      vatCents: Number(v.vat_cents ?? 0),
    })),
    chainHash: r.chain_hash,
    lines: (r.pms_fiscal_line ?? [])
      .slice()
      .sort((a, b) => a.line_no - b.line_no)
      .map((l) => ({
        id: l.id,
        lineNo: l.line_no,
        kind: l.kind,
        labelFr: l.label_fr,
        quantity: Number(l.quantity),
        unitCents: Number(l.unit_cents),
        amountCents: Number(l.amount_cents),
        tvaRate: rate(l.tva_rate),
        serviceStart: l.service_start,
        serviceEnd: l.service_end,
        creditsLineId: l.credits_line_id,
      })),
  };
}

export class SupabaseInvoicing implements InvoicingService {
  constructor(
    private readonly db: SupabaseClient,
    private readonly actor: ActorSource,
  ) {}

  async issueInvoice(folioId: FolioId, through?: IsoDate): Promise<Result<FiscalDocumentId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_INVOICE_WRITE);
    if (denied) return denied;
    return fromResult<FiscalDocumentId>(
      await this.db.rpc('pms_issue_invoice', { p_folio_id: folioId, ...(through ? { p_through: through } : {}) }),
    );
  }

  async issueCreditNote(documentId: FiscalDocumentId, lineIds?: FiscalLineId[]): Promise<Result<FiscalDocumentId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_INVOICE_CREDIT);
    if (denied) return denied;
    return fromResult<FiscalDocumentId>(
      await this.db.rpc('pms_issue_credit_note', {
        p_document_id: documentId,
        ...(lineIds && lineIds.length > 0 ? { p_line_ids: lineIds } : {}),
      }),
    );
  }

  async documentsForFolio(folioId: FolioId): Promise<Result<FiscalDocument[]>> {
    const r = await this.db
      .from('pms_fiscal_document')
      .select(DOCUMENT_SELECT)
      .eq('folio_id', folioId)
      .order('sequence_number', { ascending: true });
    return fromList<DocumentRow, FiscalDocument>(
      r as unknown as { data: DocumentRow[] | null; error: typeof r.error },
      rowToDocument,
    );
  }
}

interface PaymentRow {
  id: number;
  folio_id: number;
  movement: Payment['movement'];
  tender: Payment['tender'];
  amount_cents: number;
  is_training: boolean;
  terminal_transaction_id: number | null;
  receipt_ref: string | null;
  source_payment_id: number | null;
  vat_returned_cents: number | null;
  reason: string | null;
  authorised_at: string;
  posted_at: string;
}

const PAYMENT_SELECT =
  'id, folio_id, movement, tender, amount_cents, is_training, terminal_transaction_id, receipt_ref, ' +
  'source_payment_id, vat_returned_cents, reason, authorised_at, posted_at';

const rowToPayment = (r: PaymentRow): Payment => ({
  id: r.id,
  folioId: r.folio_id,
  movement: r.movement,
  tender: r.tender,
  amountCents: Number(r.amount_cents),
  isTraining: r.is_training,
  terminalTransactionId: r.terminal_transaction_id,
  receiptRef: r.receipt_ref,
  sourcePaymentId: r.source_payment_id,
  vatReturnedCents: r.vat_returned_cents === null ? null : Number(r.vat_returned_cents),
  reason: r.reason,
  authorisedAt: r.authorised_at,
  postedAt: r.posted_at,
});

/** The checkout RPC's shape for one payment (`pms_checkout_folio_work`). */
const paymentJson = (p: CheckoutPayment) =>
  'terminalTransactionId' in p
    ? { terminal_transaction_id: p.terminalTransactionId }
    : { tender: p.tender, amount_cents: p.amountCents, ...(p.receiptRef ? { receipt_ref: p.receiptRef } : {}) };

export class SupabasePayments implements PaymentService {
  constructor(
    private readonly db: SupabaseClient,
    private readonly actor: ActorSource,
  ) {}

  async receive(folioId: FolioId, payment: CheckoutPayment): Promise<Result<PaymentId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_PAYMENT_WRITE);
    if (denied) return denied;
    const args =
      'terminalTransactionId' in payment
        ? { p_folio_id: folioId, p_tender: 'card', p_terminal_transaction_id: payment.terminalTransactionId }
        : {
            p_folio_id: folioId,
            p_tender: payment.tender,
            p_amount_cents: payment.amountCents,
            ...(payment.receiptRef ? { p_receipt_ref: payment.receiptRef } : {}),
          };
    return fromResult<PaymentId>(await this.db.rpc('pms_receive_payment', args));
  }

  async reverse(paymentId: PaymentId, reason: string): Promise<Result<PaymentId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_PAYMENT_WRITE);
    if (denied) return denied;
    return fromResult<PaymentId>(await this.db.rpc('pms_reverse_payment', { p_payment_id: paymentId, p_reason: reason }));
  }

  async refund(
    paymentId: PaymentId,
    reason: string,
    via: { terminalTransactionId: TerminalTransactionId } | { amountCents: Cents; receiptRef?: string },
  ): Promise<Result<PaymentId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_PAYMENT_WRITE);
    if (denied) return denied;
    const rest =
      'terminalTransactionId' in via
        ? { p_terminal_transaction_id: via.terminalTransactionId }
        : { p_amount_cents: via.amountCents, ...(via.receiptRef ? { p_receipt_ref: via.receiptRef } : {}) };
    return fromResult<PaymentId>(
      await this.db.rpc('pms_refund_payment', { p_payment_id: paymentId, p_reason: reason, ...rest }),
    );
  }

  async transfer(paymentId: PaymentId, toFolioId: FolioId, amountCents: Cents, reason: string): Promise<Result<PaymentId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_PAYMENT_WRITE);
    if (denied) return denied;
    return fromResult<PaymentId>(
      await this.db.rpc('pms_transfer_payment', {
        p_payment_id: paymentId,
        p_to_folio_id: toFolioId,
        p_amount_cents: amountCents,
        p_reason: reason,
      }),
    );
  }

  async forfeit(paymentId: PaymentId, reason: string, amountCents?: Cents): Promise<Result<PaymentId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_PAYMENT_FORFEIT);
    if (denied) return denied;
    return fromResult<PaymentId>(
      await this.db.rpc('pms_forfeit_payment', {
        p_payment_id: paymentId,
        p_reason: reason,
        ...(amountCents === undefined ? {} : { p_amount_cents: amountCents }),
      }),
    );
  }

  async checkout(folioId: FolioId, payments: CheckoutPayment[], through?: IsoDate): Promise<Result<FiscalDocumentId>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_PAYMENT_WRITE) ?? gate(this.actor, CAPABILITIES.PMS_INVOICE_WRITE);
    if (denied) return denied;
    return fromResult<FiscalDocumentId>(
      await this.db.rpc('pms_checkout_folio', {
        p_folio_id: folioId,
        p_payments: payments.map(paymentJson),
        ...(through ? { p_through: through } : {}),
      }),
    );
  }

  async position(folioId: FolioId): Promise<Result<FolioPosition>> {
    const r = await this.db.rpc('pms_folio_position', { p_folio_id: folioId });
    if (r.error) return fromResult<FolioPosition>(r as { data: null; error: typeof r.error });
    const v = (r.data ?? null) as Record<string, number | string> | null;
    if (v === null) return err('unknown', 'Note : position illisible', { retryable: false });
    return ok({
      unbilledCents: Number(v.unbilledCents ?? 0),
      invoicedOpenCents: Number(v.invoicedOpenCents ?? 0),
      unappliedCents: Number(v.unappliedCents ?? 0),
    });
  }

  async paymentsForFolio(folioId: FolioId): Promise<Result<Payment[]>> {
    const r = await this.db
      .from('pms_payment')
      .select(PAYMENT_SELECT)
      .eq('folio_id', folioId)
      .order('posted_at', { ascending: true })
      .order('id', { ascending: true });
    return fromList<PaymentRow, Payment>(
      r as unknown as { data: PaymentRow[] | null; error: typeof r.error },
      rowToPayment,
    );
  }
}
